import React from 'react';
import { useGetFeatureFlagsQuery } from '../../api/featuresApi';
import type { FeatureFlags } from '../../api/featuresApi';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import Spinner from '../Spinner/Spinner';

interface Props {
    flag: keyof FeatureFlags;
    /** Rendered when the flag is ON */
    children: React.ReactNode;
    /** Optional: rendered when the flag is OFF (falls back to the existing page) */
    fallback?: React.ReactNode;
}

/**
 * FeatureGate — server-driven feature flag gate.
 *
 * Renders <Spinner /> while the flag query is in-flight, then renders
 * `children` if the flag is enabled or `fallback` (default: null) if not.
 *
 * The `flag` prop is `keyof FeatureFlags` — typos are compile errors.
 */
export function FeatureGate({ flag, children, fallback = null }: Props) {
    const { isLoading } = useGetFeatureFlagsQuery();
    const flags = useFeatureFlags();

    if (isLoading) return <Spinner />;
    return flags[flag] ? <>{children}</> : <>{fallback}</>;
}
